import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { camelizeKeys } from "humps";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { PushMessageGroup } from "admin/types/pushMessageGroup";
import { RootState } from "admin/store";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { PageContext } from "admin/types/pageContext";
import { AdminTypes } from "admin/constants/adminTypes";
import { Meta } from "admin/types/meta";
import {
  isObjectEmpty,
  objectToFormData,
  removeEmpty,
} from "admin/utils/objectUtil";
import { HotelPushMessageGroupMessageTypes } from "admin/constants/hotelPushMessageGroupMessageTypes";
import { convertUrlToFile } from "admin/utils/blobUtil";

const key = "hotelPushMessageGroup";
const adapter = createEntityAdapter<PushMessageGroup.PushMessageGroup, number>({
  selectId: (pushMessageGroup) => pushMessageGroup.id,
});

const initialStateValues: PushMessageGroup.PushMessageGroupState = {
  pushMessageGroup: null,
  pushMessageIndex: null,
  recipientCount: null,
  errors: null,
  loading: false,
  meta: null,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async **/
export const fetchPushMessageGroups = createAsyncThunk<
  {
    pushMessageGroups: PushMessageGroup.PushMessageGroup[];
    meta: Meta;
  },
  {
    context: PageContext.PageContext;
    hotelId: string;
    param?: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchPushMessageGroups`, async (args, { rejectWithValue }) => {
  try {
    const {
      context,
      hotelId,
      param,
      isServerSide = false,
      headers,
      locale,
    } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/push_message_groups.json?${param}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/push_message_groups.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      pushMessageGroups: result.data.pushMessageGroups,
      meta: result.data.meta,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchPushMessageGroup = createAsyncThunk<
  {
    pushMessageGroup: PushMessageGroup.PushMessageGroup;
  },
  {
    context: PageContext.PageContext;
    id: number;
    hotelId: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchPushMessageGroup`, async (args, { rejectWithValue }) => {
  try {
    const {
      context,
      hotelId,
      id,
      isServerSide = false,
      headers,
      locale,
    } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/push_message_groups/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/push_message_groups/${id}.json`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      pushMessageGroup: result.data.pushMessageGroup,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postPushMessageGroup = createAsyncThunk<
  { pushMessageGroup: PushMessageGroup.PushMessageGroup },
  { context: PageContext.PageContext; hotelId: string; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postPushMessageGroup`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/push_message_groups.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/push_message_groups.json`;
    const formData: any = new FormData();
    objectToFormData(params, formData, "push_message_group");
    const result = await createWrapper({ isMultipart: true }).post(
      url,
      formData,
    );
    return { pushMessageGroup: result.data.pushMessageGroup };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putPushMessageGroup = createAsyncThunk<
  { pushMessageGroup: PushMessageGroup.PushMessageGroup },
  {
    context: PageContext.PageContext;
    id: number;
    hotelId: string;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putPushMessageGroup`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, hotelId, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/push_message_groups/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/push_message_groups/${id}.json`;
    const formData: any = new FormData();
    objectToFormData(params, formData, "push_message_group");
    const result = await createWrapper({ isMultipart: true }).put(
      url,
      formData,
    );
    return { pushMessageGroup: result.data.pushMessageGroup };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchRecipientCount = createAsyncThunk<
  {
    count: number;
  },
  {
    context: PageContext.PageContext;
    param?: string;
    hotelId: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchRecipientCount`, async (args, { rejectWithValue }) => {
  try {
    const {
      context,
      hotelId,
      param,
      isServerSide = false,
      headers,
      locale,
    } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/message_recipients.json?${param}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/message_recipients.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      count: result.data.recipientsCount,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const sendPushMessageGroup = createAsyncThunk<
  void,
  { context: PageContext.PageContext; id: number; hotelId: string },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/sendPushMessageGroup`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, hotelId } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/push_message_groups/${id}/bulk_pushing.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/push_message_groups/${id}/bulk_pushing.json`;
    await createWrapper().post(url);
    return;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice **/
export const hotelPushMessageGroupSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    setPushMessageGroup: (state, action) => {
      state.pushMessageGroup = action.payload;
    },
    setPushMessage: (state, action) => {
      state.pushMessage = action.payload.pushMessage;
      state.pushMessageIndex = action.payload.index;
    },
    clearPushMessage: (state) => {
      state.pushMessage = null;
      state.pushMessageIndex = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ページ間でメッセージコンテンツデータを共有するためHYDRATEしない
      // もしページ単位でサーバーにPOSTするならHYDRATEしてSSRでメッセージデータを取得する
      .addCase(HYDRATE, (state, action: any) => {
        const data = action.payload.hotelPushMessageGroup;
        const pushMessageGroup =
          state.pushMessageGroup && !isObjectEmpty(state.pushMessageGroup)
            ? state.pushMessageGroup
            : data.pushMessageGroup;
        return {
          ...state,
          ...action.payload.hotelPushMessageGroup,
          pushMessageGroup,
        };
      })
      .addCase(fetchPushMessageGroups.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.pushMessageGroups);
        state.meta = action.payload.meta;
      })
      .addCase(fetchPushMessageGroup.fulfilled, (state, action) => {
        state.pushMessageGroup = action.payload.pushMessageGroup;
      })
      .addCase(postPushMessageGroup.fulfilled, (state, action) => {
        state.pushMessageGroup = action.payload.pushMessageGroup;
      })
      .addCase(putPushMessageGroup.fulfilled, (state, action) => {
        state.pushMessageGroup = action.payload.pushMessageGroup;
      })
      .addCase(fetchRecipientCount.fulfilled, (state, action) => {
        state.recipientCount = action.payload.count;
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: pushMessageGroupByIdSelector,
  selectIds: pushMessageGroupIdsSelector,
  selectEntities: pushMessageGroupEntitiesSelector,
  selectAll: pushMessageGroupsSelector,
  selectTotal: totalPushMessageGroupsSelector,
} = adapter.getSelectors((state: RootState) => state.hotelPushMessageGroup);

const stateSelector = (state: RootState) => state[key];

export const pushMessageGroupSelector = createSelector(
  stateSelector,
  (state) => state.pushMessageGroup,
);

export const pushMessageSelector = createSelector(
  stateSelector,
  (state) => state.pushMessage,
);

export const pushMessageIndexSelector = createSelector(
  stateSelector,
  (state) => state.pushMessageIndex,
);

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const pushMessageGroupsMetaSelector = createSelector(
  stateSelector,
  (state) => state.meta,
);

export const recipientCountSelector = createSelector(
  stateSelector,
  (state) => state.recipientCount,
);

/** action export **/
export const { setPushMessageGroup, setPushMessage, clearPushMessage } =
  hotelPushMessageGroupSlice.actions;

export const convertPushMessageGroupFormValuesToPushMessageGroup = (
  data: any,
) => {
  const pushMessageGroup: PushMessageGroup.PushMessageGroup = {
    ...camelizeKeys(data),
  };
  return pushMessageGroup;
};

export const convertPushMessageGroupMessageFormValuesToPushMessage = (
  data: any,
) => {
  const pushMessage = {
    ...data,
  };
  const objectUrls = [];
  if (pushMessage.message_type === HotelPushMessageGroupMessageTypes.image) {
    const file = pushMessage.image.value?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      pushMessage.image = { value: url, name: file.name, type: file.type };
      objectUrls.push(url);
    }
  } else if (
    pushMessage.message_type === HotelPushMessageGroupMessageTypes.button
  ) {
    const file = pushMessage.button.image?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      pushMessage.button.image = {
        value: url,
        name: file.name,
        type: file.type,
      };
      objectUrls.push(url);
    }
  } else if (
    pushMessage.message_type === HotelPushMessageGroupMessageTypes.carousels
  ) {
    pushMessage.carousels = pushMessage.carousels.map((column) => {
      const file = column.image?.[0];
      if (file) {
        const url = URL.createObjectURL(file);
        column.image = {
          value: url,
          name: file.name,
          type: file.type,
        };
        objectUrls.push(url);
      }
      return column;
    });
  }
  return [camelizeKeys(pushMessage), objectUrls];
};

export const convertPushMessageToFormValues = async (data: any) => {
  const values = {
    ...data,
  };
  if (values.messageType === HotelPushMessageGroupMessageTypes.image) {
    const image = values.image;
    if (image && image.value && image.name) {
      const file = await convertUrlToFile(image.value, image.name, image.type);
      values.image = { value: [file] };
    }
  } else if (
    values.messageType === HotelPushMessageGroupMessageTypes.externalImage
  ) {
    if (values.externalImage.originalValueUrl) {
      const url = values.externalImage.originalValueUrl;
      values.externalImage = { value: url };
    }
  } else if (values.messageType === HotelPushMessageGroupMessageTypes.button) {
    const image = values.button?.image;
    if (image && image.value && image.name) {
      const file = await convertUrlToFile(image.value, image.name, image.type);
      values.button = { ...values.button, image: [file] };
    }
  } else if (
    values.messageType === HotelPushMessageGroupMessageTypes.carousels
  ) {
    values.carousels = await Promise.all(
      values.carousels.map(async (column) => {
        const newColumn = { ...column };
        const image = newColumn.image;
        if (image && image.value && image.name) {
          const file = await convertUrlToFile(
            image.value,
            image.name,
            image.type,
          );
          newColumn.image = [file];
          return newColumn;
        }
        return column;
      }),
    );
  }
  return values;
};

export const convertPushMessageGroupFormValuesToParams = async (data: any) => {
  const params = { ...data };
  if (params.recipient_filter) {
    params.filter_json = JSON.stringify(removeEmpty(params.recipient_filter));
    delete params.recipient_filter;
  }
  params.push_messages = await Promise.all(
    params.push_messages.map(async (pushMessage, index) => {
      const newPushMessage = { ...pushMessage };
      if (
        newPushMessage.message_type === HotelPushMessageGroupMessageTypes.image
      ) {
        const image = newPushMessage.image;
        if (image && image.value && image.name) {
          const file = await convertUrlToFile(
            image.value,
            image.name,
            image.type,
          );
          newPushMessage.image = { value: file };
        } else {
          // [image] 変更しない場合は"null"を設定する
          newPushMessage.image = { value: "null" };
        }
      } else if (
        newPushMessage.message_type ===
        HotelPushMessageGroupMessageTypes.externalImage
      ) {
        const externalImage = newPushMessage.external_image;
        if (
          externalImage &&
          externalImage.value &&
          typeof externalImage.value === "object"
        ) {
          const originalValueUrl = externalImage.original_value_url;
          // [externalImage] 変更しない場合は"originalValueUrl"をvalueに再設定する
          newPushMessage.external_image = { value: originalValueUrl };
        }
      } else if (
        newPushMessage.message_type === HotelPushMessageGroupMessageTypes.button
      ) {
        const image = newPushMessage.button?.image;
        if (image && image.value && image.name) {
          const file = await convertUrlToFile(
            image.value,
            image.name,
            image.type,
          );
          newPushMessage.button.image = file;
        } else if (!newPushMessage.button?.image_url) {
          // [button] 削除する場合は"null"を設定する
          newPushMessage.button.image = "null";
        } else {
          // [button] 変更しない場合はimage keyを削除する
          delete newPushMessage.button.image;
        }
      } else if (
        newPushMessage.message_type ===
        HotelPushMessageGroupMessageTypes.carousels
      ) {
        newPushMessage.carousels = await Promise.all(
          newPushMessage.carousels.map(async (column) => {
            const image = column.image;
            if (image && image.value && image.name) {
              const file = await convertUrlToFile(
                image.value,
                image.name,
                image.type,
              );
              column.image = file;
            } else {
              // [button] 変更しない場合はimage keyを削除する
              delete column.image;
            }
            return column;
          }),
        );
      }
      newPushMessage.order = index;
      return newPushMessage;
    }),
  );
  return params;
};

export const convertPushMessageGroupToFormValues = (data: any) => {
  const values = { ...data };
  if (values.filterJson) {
    values.recipientFilter = JSON.parse(values.filterJson);
    delete values.filterJson;
  }
  return values;
};
